import type { Filter, FilterDefault } from "common/types/filters";
import type { Role } from "common/types/roles";
import type { Site } from "common/types/sites";
import type { FilterDefaultListItem } from "common/filter/form/defaults";

export const getAllDefaultCombinations = (
  sites: string[],
  roleIds: number[],
): FilterDefault[] =>
  sites.map((site) => ({
    site,
    roleIds,
  }));

export const getRoleLabel = (allRoles: Role[], roleId: number) =>
  allRoles.find((r) => r.id === roleId)?.name ?? roleId;

export const getAvailableRoles = (allRoles: Role[], filterRoleIds: number[]) =>
  filterRoleIds.length ? filterRoleIds : allRoles.map((r) => r.id);

const isSiteRestrictedFromSelection = (
  defaults: FilterDefault[],
  siteName: string,
  availableRoleIds: number[],
) =>
  defaults?.some(
    (v) =>
      v.site === siteName &&
      availableRoleIds.every((role) => v.roleIds.includes(role)),
  );

export const getSitesToOmit = (
  allSites: Site[],
  availableRoleIds: number[],
  filter: Filter,
) =>
  allSites.filter(
    (site) =>
      !filter.sites.includes(site.name) ||
      isSiteRestrictedFromSelection(
        filter.defaults,
        site.name,
        availableRoleIds,
      ),
  );

export const getRolesForSelectedSites = (
  selectedSites: string[],
  allRoles: Role[],
  filter: Filter,
) => {
  const { roleIds, defaults } = filter;

  if (selectedSites.length === 0) return [];

  return getAvailableRoles(allRoles, roleIds)
    .filter(
      (roleId) =>
        !defaults.some(
          (v) =>
            v.site === selectedSites[0] &&
            v.roleIds.some((id) => id === roleId),
        ),
    )
    .map((roleId) => allRoles.find((r) => r.id === roleId));
};

export const getListItemsFromDefaults = (
  defaults: FilterDefault[],
): FilterDefaultListItem[] =>
  defaults.flatMap((def) =>
    def.roleIds.map((roleId) => ({ site: def.site, roleId })),
  );

export const getDefaultsFromListItems = (
  defaultsListItems: FilterDefaultListItem[] = [],
) =>
  defaultsListItems.reduce<FilterDefault[]>((acc, item) => {
    const existingItem = acc.find((r) => r.site === item.site);
    existingItem
      ? existingItem.roleIds.push(item.roleId)
      : acc.push({
          site: item.site,
          roleIds: [item.roleId],
        });
    return acc;
  }, []);
